import { useEffect, useState, useCallback, useRef } from 'react';
import { AlertTriangle, HelpCircle, X, Edit3 } from 'lucide-react';

/**
 * 전역 confirm 모달 — lib/toast.js의 confirmAction()이 dispatch하는 이벤트를 구독.
 * App.jsx 에 한 번만 렌더되면 전체 앱에서 사용 가능.
 *
 * 이벤트 규약:
 *   window.dispatchEvent(new CustomEvent('app:confirm', {
 *     detail: { message, resolve, options: { title?, confirmText?, cancelText?, variant? } }
 *   }))
 */
export default function ConfirmModal() {
  const [state, setState] = useState(null); // { message, resolve, options }
  const [visible, setVisible] = useState(false); // fade-in/out 트랜지션 상태

  const handleClose = useCallback((result) => {
    // 애니메이션 후 unmount
    setVisible(false);
    const resolve = state?.resolve;
    setTimeout(() => {
      if (resolve) resolve(result);
      setState(null);
    }, 150);
  }, [state]);

  useEffect(() => {
    const onConfirm = (e) => {
      setState(e.detail);
      // 다음 프레임에서 visible=true → 트랜지션 발동
      requestAnimationFrame(() => setVisible(true));
    };
    window.addEventListener('app:confirm', onConfirm);
    return () => window.removeEventListener('app:confirm', onConfirm);
  }, []);

  // ESC: 취소 / Enter: 확인
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose(false);
      else if (e.key === 'Enter') handleClose(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, handleClose]);

  if (!state) return null;

  const {
    message,
    options: {
      // variant='danger'면 기본 타이틀 '삭제 확인', 아니면 '확인' (개별 호출부에서 override 가능)
      title: rawTitle,
      confirmText: rawConfirmText,
      cancelText = '취소',
      variant = 'primary', // 'primary' | 'danger'
    } = {},
  } = state;
  const isDanger = variant === 'danger';
  const title = rawTitle || (isDanger ? '삭제 확인' : '확인');
  const confirmText = rawConfirmText || (isDanger ? '삭제' : '확인');

  const confirmBtnClass = isDanger
    ? 'bg-red-600 hover:bg-red-700 text-white focus:ring-2 focus:ring-red-400'
    : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-2 focus:ring-blue-400';

  const Icon = isDanger ? AlertTriangle : HelpCircle;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-opacity duration-150 ${
        visible ? 'bg-black/50 opacity-100' : 'bg-black/0 opacity-0 pointer-events-none'
      }`}
      onClick={() => handleClose(false)}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`bg-white rounded-lg shadow-xl w-full max-w-md mx-auto overflow-hidden transform transition-all duration-150 ${
          visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isDanger ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
            }`}>
              <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          </div>
          <button
            onClick={() => handleClose(false)}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="닫기"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 text-gray-700 whitespace-pre-line text-sm leading-relaxed">
          {message}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 bg-gray-50 border-t">
          <button
            type="button"
            onClick={() => handleClose(false)}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={() => handleClose(true)}
            className={`px-4 py-2 rounded-md text-sm font-medium ${confirmBtnClass}`}
            autoFocus
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Phase Q+ 모바일 UX H-1 fix: window.prompt() 대체.
 * 'app:prompt' 이벤트 구독 → textarea/input 모달 표시 → Promise<string|null> resolve.
 * App.jsx 에서 <PromptModal /> 추가 마운트 필요.
 */
export function PromptModal() {
  const [state, setState] = useState(null);
  const [visible, setVisible] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  const handleClose = useCallback((result) => {
    setVisible(false);
    const resolve = state?.resolve;
    setTimeout(() => {
      if (resolve) resolve(result);
      setState(null);
      setValue('');
    }, 150);
  }, [state]);

  useEffect(() => {
    const onPrompt = (e) => {
      setState(e.detail);
      setValue(e.detail?.options?.defaultValue || '');
      requestAnimationFrame(() => {
        setVisible(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      });
    };
    window.addEventListener('app:prompt', onPrompt);
    return () => window.removeEventListener('app:prompt', onPrompt);
  }, []);

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose(null);
      else if (e.key === 'Enter' && !e.shiftKey && !state?.options?.multiline) {
        e.preventDefault();
        if (canSubmit) handleClose(value);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, value, handleClose]);

  if (!state) return null;

  const {
    message,
    options: {
      title: rawTitle,
      confirmText = '확인',
      cancelText = '취소',
      placeholder = '',
      minLength = 0,
      multiline = false,
      variant = 'primary',
    } = {},
  } = state;
  const isDanger = variant === 'danger';
  const title = rawTitle || '입력';
  const canSubmit = value.trim().length >= minLength;

  const confirmBtnClass = isDanger
    ? 'bg-red-600 hover:bg-red-700 text-white focus:ring-2 focus:ring-red-400'
    : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-2 focus:ring-blue-400';

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center sm:items-center justify-center transition-opacity duration-150 ${
        visible ? 'bg-black/50 opacity-100' : 'bg-black/0 opacity-0 pointer-events-none'
      }`}
      onClick={() => handleClose(null)}
      role="dialog"
      aria-modal="true"
    >
      {/* 모바일: 하단 시트 / 데스크탑: 중앙 모달 */}
      <div
        className={`bg-white shadow-xl w-full sm:max-w-md sm:mx-auto overflow-hidden transform transition-all duration-150 sm:rounded-lg ${
          visible ? 'translate-y-0 sm:scale-100 opacity-100' : 'translate-y-full sm:translate-y-0 sm:scale-95 opacity-0'
        } rounded-t-2xl sm:rounded-2xl absolute bottom-0 sm:relative`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isDanger ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
            }`}>
              <Edit3 className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          </div>
          <button
            onClick={() => handleClose(null)}
            className="text-gray-400 hover:text-gray-600 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="닫기"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4">
          <label className="block text-sm text-gray-700 whitespace-pre-line mb-2">
            {message}
          </label>
          {multiline ? (
            <textarea
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            />
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          )}
          {minLength > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              최소 {minLength}자 이상 입력 ({value.trim().length}/{minLength})
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 bg-gray-50 border-t">
          <button
            type="button"
            onClick={() => handleClose(null)}
            className="px-4 py-2.5 min-h-[44px] rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={() => canSubmit && handleClose(value)}
            disabled={!canSubmit}
            className={`px-4 py-2.5 min-h-[44px] rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${confirmBtnClass}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
